import { useMemo } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import { Check } from '@mui/icons-material';

export const cardStyles = {
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: { xxs: '900px', md: '960px' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xxs: '20px', sm: '32px' },
  },
  cardsWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    position: 'relative',
    border: '1px solid #C7D0DE',
    borderRadius: '18px',
    backgroundColor: '#FFFFFF',
    padding: '36px 18px 22px 18px',
    width: '280px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xxs: '14px', sm: '16px' },
    overflow: 'hidden',
    '& > [data-card-accent]': {
      zIndex: 0,
    },
    '& > :not([data-card-accent])': {
      // position: 'relative',
      zIndex: 1,
    },
  },
  tier: {
    fontSize: { xxs: '24px', sm: '26px' },
    fontWeight: 800,
    color: '#232B35',
    letterSpacing: '-0.43px',
  },
  price: {
    fontSize: { xxs: '30px', sm: '32px' },
    fontWeight: 800,
    color: '#232B35',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '6px',
  },
  priceSuffix: {
    fontSize: '10px',
    fontWeight: 400,
    color: '#232B35',
    textTransform: 'lowercase',
  },
  button: {
    borderRadius: '28px',
    textTransform: 'none',
    width: '180px',
    height: '44px',
    fontWeight: 700,
    fontSize: '15px',
    backgroundColor: '#3171F6',
    '&:hover': {
      backgroundColor: '#1F62F1',
    },
    '&.Mui-disabled': {
      backgroundColor: '#C7D0DE',
      color: '#FFFFFF',
    },
  },
  list: {
    width: '100%',
    // marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    color: '#232B35',
    textAlign: 'left',
    lineHeight: '18px',
    letterSpacing: '-0.43px',
    fontWeight: 400,
  },
  tag: {
    position: 'absolute',
    top: '12px',
    right: '18px',
    padding: '2px 12px',
    borderRadius: '8px',
    backgroundColor: '#3171F6',
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: 600,
  },
  accentCircle: {
    position: 'absolute',
    borderRadius: '50%',
    opacity: 0.75,
    pointerEvents: 'none',
  },
};

export const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '--';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: numeric % 1 === 0 ? 0 : 2,
    maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
  }).format(numeric);
};

export const formatBillingCycle = (term, fallback) => {
  if (term === 'mo') {
    return 'month';
  }
  if (term === 'yr') {
    return 'year';
  }
  return fallback || term || '';
};

export const getMaxAdvisorCount = (plan = {}) => {
  const { maxAdvisorCount, boardsCanJoinCount, salesforceValues } = plan;
  return (
    maxAdvisorCount ??
    boardsCanJoinCount ??
    salesforceValues?.maxAdvisorCount ??
    ''
  );
};

export const buildFeatures = (plan, featureFactories) =>
  featureFactories
    .map((factory) => factory(plan))
    .filter((value) => typeof value === 'string' && value.trim().length > 0);

export const planFeatureFactories = {
  bronze: [
    () => 'Secure Digital Boardroom',
    () => 'Access to our network of advisors',
    () => '1-1 onboarding call',
    (plan) => {
      const count = getMaxAdvisorCount(plan);
      return count
        ? `Up to ${count} advisors on your board`
        : 'Grow your advisory board';
    },
  ],
  silver: [
    () => 'Everything from Bronze, plus:',
    () => 'Get featured on our platform',
    () => 'Leverage introductions from advisors',
    (plan) => {
      const count = getMaxAdvisorCount(plan);
      return count
        ? `Up to ${count} advisory board members`
        : 'Expand your advisory board';
    },
  ],
  gold: [
    () => 'Everything from Silver, plus:',
    () => 'Dedicated account manager',
    () => 'Active board management and outreach',
    () => 'Dedicated deep dives with your advisors',
    (plan) => {
      const count = getMaxAdvisorCount(plan);
      return count
        ? `Up to ${count} advisory board members`
        : 'Unlock premium board growth';
    },
  ],
};

export const planTagMap = {
  bronze: null,
  silver: 'Most popular!',
  gold: null,
};

export const planAccentMap = {
  bronze: {
    color: '#d7f1fd',
    position: { width: '114px', height: '114px', top: '-47px', left: '-49px' },
  },
  silver: {
    color: '#dbf7eb',
    position: { width: '114px', height: '114px', top: '55px', right: '-70px' },
  },
  gold: {
    color: '#ffeed6',
    position: {
      width: '114px',
      height: '114px',
      bottom: '-47px',
      right: '-49px',
    },
  },
};

const PlanCard = ({
  tag,
  tier,
  price,
  billingCycle,
  features,
  disabled,
  onSelect,
  buttonId,
  accent,
}) => (
  <Box sx={cardStyles.card}>
    {accent ? (
      <Box
        data-card-accent
        aria-hidden
        sx={{
          ...cardStyles.accentCircle,
          backgroundColor: accent.color,
          ...accent.position,
        }}
      />
    ) : null}
    {tag ? <Typography sx={cardStyles.tag}>{tag}</Typography> : null}
    <Box>
      <Typography sx={cardStyles.tier}>{tier || '--'}</Typography>
      <Typography sx={cardStyles.price}>
        {price}
        {billingCycle ? (
          <Typography component="span" sx={cardStyles.priceSuffix}>
            /{billingCycle}
          </Typography>
        ) : null}
      </Typography>
    </Box>
    <Box sx={cardStyles.list}>
      {features.map((feature, index) => (
        <Typography component="div" key={index} sx={cardStyles.listItem}>
          <Check sx={{ fontSize: '18px', marginTop: '2px' }} />
          <span>{feature}</span>
        </Typography>
      ))}
    </Box>
    <Button
      variant="contained"
      sx={cardStyles.button}
      onClick={onSelect}
      disabled={disabled}
      id={buttonId}
    >
      Upgrade
    </Button>
  </Box>
);

export default function Step1({
  onContinue,
  onOptionChange,
  onClose,
  freemiumUpgradeOnePlan,
  freemiumUpgradeTwoPlan,
  acPlusUpgradeOptionOnePlan,
}) {
  const plans = useMemo(
    () => [
      {
        id: 'bronze',
        planDetails: freemiumUpgradeOnePlan,
        tag: planTagMap.bronze,
        buttonId: 'freemiumUpgradeOne',
        featureFactories: planFeatureFactories.bronze,
        accent: planAccentMap.bronze,
      },
      {
        id: 'silver',
        planDetails: freemiumUpgradeTwoPlan,
        tag: planTagMap.silver,
        buttonId: 'freemiumUpgradeTwo',
        featureFactories: planFeatureFactories.silver,
        accent: planAccentMap.silver,
      },
      {
        id: 'gold',
        planDetails: acPlusUpgradeOptionOnePlan,
        tag: planTagMap.gold,
        buttonId: 'acPlusUpgradeOptionOne',
        featureFactories: planFeatureFactories.gold,
        accent: planAccentMap.gold,
      },
    ],
    [freemiumUpgradeOnePlan, freemiumUpgradeTwoPlan, acPlusUpgradeOptionOnePlan]
  );

  const renderPlans = plans.map((config) => {
    const plan = config.planDetails || {};
    const tier = plan?.salesforceValues?.tier || '';
    const billingCycle = formatBillingCycle(
      plan?.recurlyPlanCodeTerm,
      plan?.salesforceValues?.billingCycle
    );
    const price = formatPrice(plan?.recurlyPlanCodePrice);
    const features = buildFeatures(plan, config.featureFactories);
    const disabled = !plan?.recurlyPlanCode;

    const handleSelect = () => {
      if (disabled) {
        return;
      }
      onOptionChange?.({ ...plan, type: config.id });
      onContinue?.(plan);
    };

    return (
      <Grid
        key={config.id}
        item
        xs={12}
        sm={6}
        md={4}
        display="flex"
        justifyContent="center"
      >
        <PlanCard
          tier={tier}
          price={price}
          billingCycle={billingCycle}
          features={features}
          tag={config.tag}
          disabled={disabled}
          onSelect={handleSelect}
          buttonId={config.buttonId}
          accent={config.accent}
        />
      </Grid>
    );
  });

  return (
    <Box
      sx={{
        ...cardStyles.container,
        padding: { xxs: '32px 20px', sm: '0px' },
      }}
    >
      <Typography
        sx={{
          color: '#151F33',
          fontSize: { xxs: '22px', sm: '28px' },
          fontWeight: 800,
          letterSpacing: '-0.43px',
          textAlign: 'center',
        }}
      >
        Upgrade and unlock the potential of your advisory board
      </Typography>

      <Box sx={cardStyles.cardsWrapper}>
        <Grid
          container
          spacing={{ xxs: 3, sm: 4 }}
          justifyContent="center"
          alignItems="flex-start"
        >
          {renderPlans}
        </Grid>
      </Box>

      <Typography
        id="companyFreemiumCustomSolutionCta"
        role="button"
        tabIndex={0}
        onClick={() => onClose?.()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClose?.();
          }
        }}
        sx={{
          fontSize: { xxs: '14px', sm: '15px' },
          fontWeight: 600,
          color: '#151F33',
          textDecoration: 'underline',
          cursor: 'pointer',
          marginTop: { xxs: '12px', sm: '18px' },
        }}
      >
        Looking for a custom solution? Contact us.
      </Typography>
    </Box>
  );
}
